import { useState, useMemo, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useReferralLeads } from "@/hooks/useReferralLeads";
import { usePaginatedLeads } from "@/hooks/usePaginatedLeads";
import { usePaginatedTasks } from "@/hooks/usePaginatedTasks";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { CalendarIcon, Printer, Download, Mail, Loader2 } from "lucide-react";
import type { OutreachTask } from "@/hooks/useOutreachTasks";
import type { DbLead } from "@/lib/types";
import { cn } from "@/lib/utils";
import { format, startOfWeek, endOfWeek, subWeeks, subDays, isWithinInterval, parseISO } from "date-fns";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";

const ROWS_PER_PAGE = 10;

export default function Reports() {
  const navigate = useNavigate();
  const today = new Date();
  const [startDate, setStartDate] = useState<Date>(startOfWeek(today, { weekStartsOn: 1 }));
  const [endDate, setEndDate] = useState<Date>(endOfWeek(today, { weekStartsOn: 1 }));
  const [generatingPdf, setGeneratingPdf] = useState(false);
  const [leadsPage, setLeadsPage] = useState(0);
  const [tasksPage, setTasksPage] = useState(0);
  const [selectedTask, setSelectedTask] = useState<OutreachTask | null>(null);
  const [selectedLead, setSelectedLead] = useState<DbLead | null>(null);

  const { referralLeads } = useReferralLeads();

  const setPreset = (preset: string) => {
    const now = new Date();
    switch (preset) {
      case "this_week":
        setStartDate(startOfWeek(now, { weekStartsOn: 1 }));
        setEndDate(endOfWeek(now, { weekStartsOn: 1 }));
        break;
      case "last_week":
        setStartDate(startOfWeek(subWeeks(now, 1), { weekStartsOn: 1 }));
        setEndDate(endOfWeek(subWeeks(now, 1), { weekStartsOn: 1 }));
        break;
      case "last_30":
        setStartDate(subDays(now, 30));
        setEndDate(now);
        break;
    }
  };

  // Reset to page 1 whenever the report period changes.
  useEffect(() => { setLeadsPage(0); setTasksPage(0); }, [startDate, endDate]);

  const dateFromIso = startDate.toISOString();
  const dateToIso = endDate.toISOString();
  const dateFromDay = format(startDate, "yyyy-MM-dd");
  const dateToDay = format(endDate, "yyyy-MM-dd");

  const {
    leads: periodLeads,
    totalCount: periodLeadsCount,
    loading: leadsLoading,
  } = usePaginatedLeads({
    page: leadsPage,
    perPage: ROWS_PER_PAGE,
    search: "",
    filterScore: "all",
    filterStatus: "all",
    filterAssignee: "all",
    sortField: "created_at",
    sortDir: "desc",
    dateFrom: dateFromIso,
    dateTo: dateToIso,
  });
  const leadsTotalPages = Math.max(1, Math.ceil(periodLeadsCount / ROWS_PER_PAGE));

  const {
    tasks: upcomingTasks,
    totalCount: upcomingTasksCount,
    loading: tasksLoading,
  } = usePaginatedTasks({
    page: tasksPage,
    perPage: ROWS_PER_PAGE,
    dateFrom: dateFromDay,
    dateTo: dateToDay,
    completed: false,
  });
  const tasksTotalPages = Math.max(1, Math.ceil(upcomingTasksCount / ROWS_PER_PAGE));

  // Lightweight head-count queries for the "Tasks Completed" summary card —
  // avoids pulling every task in the account into memory just for a ratio.
  const [taskCounts, setTaskCounts] = useState({ total: 0, completed: 0 });
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const [{ count: total }, { count: completed }] = await Promise.all([
        supabase.from("outreach_tasks").select("id", { count: "exact", head: true }).gte("due_date", dateFromDay).lte("due_date", dateToDay),
        supabase.from("outreach_tasks").select("id", { count: "exact", head: true }).gte("due_date", dateFromDay).lte("due_date", dateToDay).eq("completed", true),
      ]);
      if (!cancelled) setTaskCounts({ total: total ?? 0, completed: completed ?? 0 });
    })();
    return () => { cancelled = true; };
  }, [dateFromDay, dateToDay]);

  const inRange = (dateStr: string) => {
    try {
      const d = parseISO(dateStr);
      return isWithinInterval(d, { start: startDate, end: endDate });
    } catch { return false; }
  };

  const periodReferrals = useMemo(() => referralLeads.filter(r => inRange(r.created_at)), [referralLeads, startDate, endDate]);

  const statusCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    referralLeads.forEach(r => { counts[r.status] = (counts[r.status] || 0) + 1; });
    return counts;
  }, [referralLeads]);

  const conversionRate = useMemo(() => {
    if (referralLeads.length === 0) return 0;
    const partners = referralLeads.filter(r => r.status === "Partner").length;
    return Math.round((partners / referralLeads.length) * 100);
  }, [referralLeads]);

  async function handleExportPdf() {
    setGeneratingPdf(true);
    try {
      const { data, error } = await supabase.functions.invoke("generate-weekly-report", {
        body: { start_date: startDate.toISOString(), end_date: endDate.toISOString() },
      });
      if (error) throw error;
      
      // The edge function returns HTML — create a printable window
      const printWindow = window.open("", "_blank");
      if (printWindow) {
        printWindow.document.write(data.html);
        printWindow.document.close();
        printWindow.focus();
        setTimeout(() => printWindow.print(), 500);
      }
      toast.success("Report generated");
    } catch (err) {
      // Fallback to window.print
      window.print();
      toast.info("Used browser print as fallback");
    } finally {
      setGeneratingPdf(false);
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-foreground">Weekly Report</h2>
          <p className="text-sm text-muted-foreground">Pipeline stats, upcoming tasks, and new inquiries.</p>
        </div>
        <div className="flex items-center gap-2 print:hidden">
          <Button variant="outline" size="sm" onClick={() => window.print()}>
            <Printer className="h-4 w-4 mr-1" /> Print
          </Button>
          <Button variant="outline" size="sm" onClick={handleExportPdf} disabled={generatingPdf}>
            {generatingPdf ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Download className="h-4 w-4 mr-1" />}
            PDF
          </Button>
          <Button variant="outline" size="sm" disabled title="Email domain setup required">
            <Mail className="h-4 w-4 mr-1" /> Email
          </Button>
        </div>
      </div>

      {/* Date range picker */}
      <div className="flex flex-wrap items-center gap-2 print:hidden">
        <Button variant={startDate.getTime() === startOfWeek(today, { weekStartsOn: 1 }).getTime() ? "default" : "outline"} size="sm" onClick={() => setPreset("this_week")}>This Week</Button>
        <Button variant="outline" size="sm" onClick={() => setPreset("last_week")}>Last Week</Button>
        <Button variant="outline" size="sm" onClick={() => setPreset("last_30")}>Last 30 Days</Button>
        <div className="flex items-center gap-1 ml-2">
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" size="sm" className={cn("w-[130px] justify-start text-left font-normal")}>
                <CalendarIcon className="h-3 w-3 mr-1" />
                {format(startDate, "MMM d, yyyy")}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar mode="single" selected={startDate} onSelect={d => d && setStartDate(d)} className={cn("p-3 pointer-events-auto")} />
            </PopoverContent>
          </Popover>
          <span className="text-sm text-muted-foreground">to</span>
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" size="sm" className={cn("w-[130px] justify-start text-left font-normal")}>
                <CalendarIcon className="h-3 w-3 mr-1" />
                {format(endDate, "MMM d, yyyy")}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar mode="single" selected={endDate} onSelect={d => d && setEndDate(d)} className={cn("p-3 pointer-events-auto")} />
            </PopoverContent>
          </Popover>
        </div>
      </div>

      {/* Report header for print */}
      <div className="hidden print:block text-center mb-4">
        <h1 className="text-xl font-bold">Weekly Report</h1>
        <p className="text-sm">{format(startDate, "MMM d, yyyy")} — {format(endDate, "MMM d, yyyy")}</p>
      </div>

      {/* Pipeline Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-4 pb-3 text-center">
            <div className="text-2xl font-bold text-foreground">{referralLeads.length}</div>
            <div className="text-xs text-muted-foreground">Total Referral Partners</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-3 text-center">
            <div className="text-2xl font-bold text-foreground">{periodReferrals.length}</div>
            <div className="text-xs text-muted-foreground">New This Period</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-3 text-center">
            <div className="text-2xl font-bold text-foreground">{conversionRate}%</div>
            <div className="text-xs text-muted-foreground">Conversion Rate</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-3 text-center">
            <div className="text-2xl font-bold text-foreground">{taskCounts.completed}/{taskCounts.total}</div>
            <div className="text-xs text-muted-foreground">Tasks Completed</div>
          </CardContent>
        </Card>
      </div>

      {/* Status breakdown */}
      <Card>
        <CardHeader><CardTitle className="text-base">Pipeline by Status</CardTitle></CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-3">
            {Object.entries(statusCounts).map(([status, count]) => (
              <button
                key={status}
                type="button"
                onClick={() => navigate(`/outreach?status=${encodeURIComponent(status)}`)}
                className="flex items-center gap-2 rounded-md px-1 py-0.5 transition hover:bg-muted print:pointer-events-none"
                title={`View ${status} records in Coordinator Outreach`}
              >
                <Badge variant="outline">{status}</Badge>
                <span className="text-sm font-medium">{count}</span>
              </button>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Upcoming Tasks */}
      <Card>
        <CardHeader><CardTitle className="text-base">Upcoming Tasks ({upcomingTasksCount})</CardTitle></CardHeader>
        <CardContent>
          {!tasksLoading && upcomingTasks.length === 0 ? (
            <p className="text-sm text-muted-foreground">No upcoming tasks in this period.</p>
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Task</TableHead>
                    <TableHead>Due Date</TableHead>
                    <TableHead>Type</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {upcomingTasks.map(t => (
                    <TableRow key={t.id} className="cursor-pointer hover:bg-muted/40" onClick={() => setSelectedTask(t)}>
                      <TableCell className="text-sm">{t.title}</TableCell>
                      <TableCell className="text-sm">{format(parseISO(t.due_date), "MMM d, yyyy")}</TableCell>
                      <TableCell className="text-xs text-muted-foreground">{t.sequence_type || "—"}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              {tasksTotalPages > 1 && (
                <div className="flex flex-wrap items-center justify-between gap-2 pt-3 print:hidden">
                  <span className="text-xs text-muted-foreground">{upcomingTasksCount} total</span>
                  <div className="flex items-center gap-2">
                    <Button size="sm" variant="outline" disabled={tasksPage === 0} onClick={() => setTasksPage(p => p - 1)}>Previous</Button>
                    <span className="text-sm font-semibold">{tasksPage + 1} of {tasksTotalPages}</span>
                    <Button size="sm" variant="outline" disabled={tasksPage >= tasksTotalPages - 1} onClick={() => setTasksPage(p => p + 1)}>Next</Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* New Inquiries */}
      <Card>
        <CardHeader><CardTitle className="text-base">New Inquiries ({periodLeadsCount})</CardTitle></CardHeader>
        <CardContent>
          {!leadsLoading && periodLeads.length === 0 ? (
            <p className="text-sm text-muted-foreground">No new inquiries in this period.</p>
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Score</TableHead>
                    <TableHead>Created</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {periodLeads.map(l => (
                    <TableRow key={l.id} className="cursor-pointer hover:bg-muted/40" onClick={() => setSelectedLead(l)}>
                      <TableCell className="text-sm">{l.name}</TableCell>
                      <TableCell><Badge variant="outline">{l.status}</Badge></TableCell>
                      <TableCell className="text-sm">{l.score}</TableCell>
                      <TableCell className="text-sm">{format(parseISO(l.created_at), "MMM d")}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              {leadsTotalPages > 1 && (
                <div className="flex flex-wrap items-center justify-between gap-2 pt-3 print:hidden">
                  <span className="text-xs text-muted-foreground">{periodLeadsCount} total</span>
                  <div className="flex items-center gap-2">
                    <Button size="sm" variant="outline" disabled={leadsPage === 0} onClick={() => setLeadsPage(p => p - 1)}>Previous</Button>
                    <span className="text-sm font-semibold">{leadsPage + 1} of {leadsTotalPages}</span>
                    <Button size="sm" variant="outline" disabled={leadsPage >= leadsTotalPages - 1} onClick={() => setLeadsPage(p => p + 1)}>Next</Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      <Dialog open={!!selectedTask} onOpenChange={open => { if (!open) setSelectedTask(null); }}>
        <DialogContent>
          {selectedTask && (
            <>
              <DialogHeader>
                <DialogTitle>{selectedTask.title}</DialogTitle>
              </DialogHeader>
              <div className="space-y-3 text-sm">
                {selectedTask.description && (
                  <p className="text-muted-foreground">{selectedTask.description}</p>
                )}
                <div className="grid grid-cols-2 gap-x-4 gap-y-2">
                  <div>
                    <p className="text-xs text-muted-foreground">Due Date</p>
                    <p>{format(parseISO(selectedTask.due_date), "MMM d, yyyy")}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Type</p>
                    <p>{selectedTask.sequence_type || "—"}{selectedTask.sequence_step ? ` (step ${selectedTask.sequence_step})` : ""}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Status</p>
                    <Badge variant={selectedTask.completed ? "default" : "secondary"}>
                      {selectedTask.completed ? "Completed" : "Not completed"}
                    </Badge>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Referral Partner</p>
                    <p>{referralLeads.find(r => r.id === selectedTask.referral_lead_id)?.org_name || "—"}</p>
                  </div>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!selectedLead} onOpenChange={open => { if (!open) setSelectedLead(null); }}>
        <DialogContent>
          {selectedLead && (
            <>
              <DialogHeader>
                <DialogTitle>{selectedLead.name}</DialogTitle>
              </DialogHeader>
              <div className="space-y-3 text-sm">
                <div className="grid grid-cols-2 gap-x-4 gap-y-2">
                  <div>
                    <p className="text-xs text-muted-foreground">Status</p>
                    <Badge variant="outline">{selectedLead.status}</Badge>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Score</p>
                    <p>{selectedLead.score}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">ZIP Code</p>
                    <p>{selectedLead.zip_code || "—"}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Payer Source</p>
                    <p>{selectedLead.payer_source || "—"}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Urgency</p>
                    <p>{selectedLead.urgency ?? "—"}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Hours/Week</p>
                    <p>{selectedLead.hours_needed ?? "—"}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Created</p>
                    <p>{format(parseISO(selectedLead.created_at), "MMM d, yyyy")}</p>
                  </div>
                </div>
                {selectedLead.acuity_notes && (
                  <div>
                    <p className="text-xs text-muted-foreground">Care Needs / Notes</p>
                    <p className="text-muted-foreground">{selectedLead.acuity_notes}</p>
                  </div>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
